import { useState, useRef, useEffect } from 'react';

// Core Session class that handles WebRTC connection and OpenAI integration
class Session {
  apiKey: string;
  mediaStream: MediaStream | null;
  peerConnection: RTCPeerConnection | null;
  dataChannel: RTCDataChannel | null;
  onconnectionstatechange?: (state: string) => void;
  onmessage?: (parsed: MessageEvent) => void;
  ontrack?: (e: RTCTrackEvent) => void;
  onopen?: () => void;
  onerror?: (e: Error) => void;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
    this.mediaStream = null;
    this.peerConnection = null;
    this.dataChannel = null;
  }

  async startTranscription(
    audioStream: MediaStream,
    sessionConfig: SessionConfig
  ): Promise<void> {
    await this.startInternal(
      audioStream,
      sessionConfig,
      '/v1/realtime/transcription_sessions'
    );
  }

  stop(): void {
    if (this.dataChannel) {
      this.dataChannel.close();
      this.dataChannel = null;
    }
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }

  async startInternal(
    audioStream: MediaStream,
    sessionConfig: SessionConfig,
    tokenEndpoint: string
  ): Promise<void> {
    this.mediaStream = audioStream;
    this.peerConnection = new RTCPeerConnection();
    this.peerConnection.ontrack = (event) => this.ontrack?.(event);
    this.peerConnection.addTrack(audioStream.getTracks()[0]);
    this.peerConnection.onconnectionstatechange = () => {
      if (this.peerConnection && this.onconnectionstatechange) {
        this.onconnectionstatechange(this.peerConnection.connectionState);
      }
    };
    this.dataChannel = this.peerConnection.createDataChannel('');
    this.dataChannel.onopen = () => this.onopen?.();
    this.dataChannel.onmessage = (event) => {
      if (this.onmessage) {
        try {
          const parsedMessage = JSON.parse(event.data);
          this.onmessage(parsedMessage);
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      }
    };
    const offer = await this.peerConnection.createOffer();
    await this.peerConnection.setLocalDescription(offer);
    try {
      const answer = await this.signal(offer, sessionConfig, tokenEndpoint);
      await this.peerConnection.setRemoteDescription(answer);
    } catch (error) {
      const errorObject =
        error instanceof Error ? error : new Error(String(error));
      this.onerror?.(errorObject);
    }
  }

  async signal(
    offer: RTCSessionDescriptionInit,
    sessionConfig: SessionConfig,
    tokenEndpoint: string
  ): Promise<RTCSessionDescriptionInit> {
    const apiBaseUrl = 'https://api.openai.com';
    const realtimeApiUrl = `${apiBaseUrl}/v1/realtime`;
    
    const sessionUrl = `${apiBaseUrl}${tokenEndpoint}`;
    const sessionResponse = await fetch(sessionUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${this.apiKey}`,
        'openai-beta': 'realtime-v1',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(sessionConfig),
    });
    
    if (!sessionResponse.ok) {
      throw new Error(
        `Failed to request session token: ${sessionResponse.statusText}`
      );
    }
    
    const sessionData = await sessionResponse.json();
    const clientSecret = sessionData.client_secret.value;
    
    const serverResponse = await fetch(`${realtimeApiUrl}`, {
      method: 'POST',
      body: offer.sdp,
      headers: {
        Authorization: `Bearer ${clientSecret}`,
        'Content-Type': 'application/sdp',
      },
    });
    
    if (!serverResponse.ok) {
      throw new Error(`Failed to signal: ${serverResponse.statusText}`);
    }

    const sdpResponseText = await serverResponse.text();
    return { type: 'answer', sdp: sdpResponseText };
  }
}

// Type definitions
interface SessionConfig {
  input_audio_transcription: {
    model: string;
    language: string;
    prompt?: string;
  };
  turn_detection: {
    type: string;
    threshold?: number;
    prefix_padding_ms?: number;
    silence_duration_ms?: number;
    create_response?: boolean;
  };
  id?: string;
}

interface MessageEvent {
  type: string;
  session?: SessionConfig;
  transcript?: string;
  delta?: string;
}

interface UseGPT4TranscriptionConfig {
  apiKey: string;
  language?: string;
  vadThreshold?: number; 
  prefixPadding?: number;
  silenceDuration?: number;
}

interface UseGPT4TranscriptionReturn {
  isListening: boolean;
  isProcessing: boolean;
  isMicAvailable: boolean; 
  isMicPermissionGranted: boolean;
  isConnectionEstablished: boolean;
  connectionStatus: string;
  partialTranscript: string;
  fullTranscript: string; 
  errorMessage: string;
  statusMessage: string;
  startListening: () => Promise<void>;
  stopListening: () => void;
  clearTranscript: () => void;
  clearFinalTranscript: () => void;
  toggleListening: () => void;
  checkMicrophoneAvailability: () => Promise<boolean>;
  requestMicrophonePermission: () => Promise<boolean>;
}

// Main hook
const useGPT4Transcription = ({
  apiKey,
  language = 'en',
  vadThreshold = 0.25,
  prefixPadding = 30,
  silenceDuration = 30
}: UseGPT4TranscriptionConfig): UseGPT4TranscriptionReturn => {
  // State variables
  const [isListening, setIsListening] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isMicAvailable, setIsMicAvailable] = useState<boolean>(false);
  const [isMicPermissionGranted, setIsMicPermissionGranted] = useState<boolean>(false);
  const [isConnectionEstablished, setIsConnectionEstablished] = useState<boolean>(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('');
  const [partialTranscript, setPartialTranscript] = useState<string>('');
  const [fullTranscript, setFullTranscript] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [statusMessage, setStatusMessage] = useState<string>('Ready to start');

  // References
  const sessionRef = useRef<Session | null>(null);
  const sessionConfigRef = useRef<SessionConfig | null>(null);
  const accumulatedTranscriptRef = useRef<string>(''); 
  const bufferedDeltasRef = useRef<string>('');

  // Check microphone availability on mount
  useEffect(() => {
    checkMicrophoneAvailability();
    
    return () => {
      if (sessionRef.current) {
        sessionRef.current.stop();
        sessionRef.current = null;
      }
    };
  }, []);

  // Format transcription helpers
  const formatTranscriptDelta = (delta: string): string => {
    let formattedDelta = delta.trim();
    formattedDelta = formattedDelta.replace(/([.!?])\s*([a-zA-Z])/g, '$1 $2');
    return formattedDelta;
  };

  const formatCompletedTranscript = (
    previousTranscript: string,
    newTranscript: string
  ): string => {
    let formattedTranscript = newTranscript.trim();

    if (previousTranscript.match(/[.!?]\s*$/)) {
      formattedTranscript =
        formattedTranscript.charAt(0).toUpperCase() +
        formattedTranscript.slice(1);
    }
    else if (
      previousTranscript.length > 0 &&
      !previousTranscript.endsWith(' ')
    ) {
      formattedTranscript =
        ' ' +
        formattedTranscript.charAt(0).toLowerCase() +
        formattedTranscript.slice(1);
    }

    formattedTranscript = formattedTranscript.replace(/\s+([,.!?:;])/g, '$1');
    formattedTranscript = formattedTranscript.replace(
      /([.!?])\s*([a-zA-Z])/g,
      '$1 $2'
    );

    return formattedTranscript;
  };

  // Handle transcription message events
  const handleMessage = (messageEvent: MessageEvent): void => {
    switch (messageEvent.type) {
      case 'transcription_session.created':
        if (messageEvent.session) {
          sessionConfigRef.current = messageEvent.session;
          setStatusMessage('Connection established');
          setIsConnectionEstablished(true);
        }
        break;

      case 'input_audio_buffer.speech_started':
        setIsProcessing(true);
        setStatusMessage('Transcribing speech...');
        break;

      case 'input_audio_buffer.speech_stopped':
        setIsProcessing(false);
        setStatusMessage('Waiting for speech...');
        break;

      case 'conversation.item.input_audio_transcription.delta':
        if (messageEvent.delta) {
          bufferedDeltasRef.current += messageEvent.delta;
          const cleanedDelta = formatTranscriptDelta(bufferedDeltasRef.current);
          setPartialTranscript(cleanedDelta);
          setIsProcessing(true);
          setStatusMessage('Transcribing speech...');
        }
        break;

      case 'conversation.item.input_audio_transcription.completed':
        if (messageEvent.transcript) {
          const formattedTranscript = formatCompletedTranscript(
            accumulatedTranscriptRef.current,
            messageEvent.transcript
          );

          accumulatedTranscriptRef.current += formattedTranscript;
          setFullTranscript(accumulatedTranscriptRef.current);
          setPartialTranscript('');
          bufferedDeltasRef.current = '';
          setIsProcessing(false);
          setStatusMessage('Waiting for speech...');
        }
        break;

      default:
        console.log('Unhandled message type:', messageEvent.type);
        break;
    }
  };

  // Handle connection errors
  const handleError = (error: Error): void => {
    console.error('Transcription error:', error);
    setErrorMessage(error.message);
    setConnectionStatus('error');
    setStatusMessage(`Error: ${error.message}`);
    stopListening();
  };

  // Core functionality methods
  const checkMicrophoneAvailability = async (): Promise<boolean> => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasMicrophone = devices.some(device => device.kind === 'audioinput');
      setIsMicAvailable(hasMicrophone);
      return hasMicrophone;
    } catch (error) {
      console.error('Error checking microphone availability:', error);
      setIsMicAvailable(false);
      return false;
    }
  };

  const requestMicrophonePermission = async (): Promise<boolean> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(track => track.stop());
      setIsMicPermissionGranted(true);
      return true;
    } catch (error) {
      console.error('Error requesting microphone permission:', error);
      setIsMicPermissionGranted(false);
      setErrorMessage('Microphone permission denied');
      setStatusMessage('Error: Microphone permission denied');
      return false;
    }
  };

  const startListening = async (): Promise<void> => {
    if (!apiKey) {
      setErrorMessage('Missing API Key');
      setStatusMessage('Error: Missing OpenAI API Key');
      return;
    }

    try {
      setStatusMessage('Requesting microphone access...');
      
      // Check microphone availability and permissions
      const hasPermission = await requestMicrophonePermission();
      if (!hasPermission) return;
      
      const microphoneStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      setIsListening(true);
      setPartialTranscript('');
      bufferedDeltasRef.current = '';
      setErrorMessage('');
      setStatusMessage('Establishing connection...');

      sessionRef.current = new Session(apiKey);
      sessionRef.current.onconnectionstatechange = (state) => {
        setConnectionStatus(state);
        if (state === 'connected') {
          setIsConnectionEstablished(true);
          setStatusMessage('Connected. Waiting for speech...');
        } else if (state === 'disconnected' || state === 'failed') {
          setIsConnectionEstablished(false);
          setStatusMessage(`Connection ${state}`);
        }
      };
      
      sessionRef.current.onmessage = (message) => handleMessage(message);
      sessionRef.current.onerror = (error) => handleError(error);

      const sessionConfig: SessionConfig = {
        input_audio_transcription: {
          model: 'gpt-4o-transcribe',
          language: language,
          prompt: "Please transcribe with accurate punctuation and capitalization, ensuring natural sentence flow."
        },
        turn_detection: {
          type: 'server_vad',
          threshold: vadThreshold,
          prefix_padding_ms: prefixPadding,
          silence_duration_ms: silenceDuration,
        },
      };

      await sessionRef.current.startTranscription(microphoneStream, sessionConfig);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setErrorMessage(errorMessage);
      setStatusMessage(`Error: ${errorMessage}`);
      setIsListening(false);
      setIsProcessing(false);
      setIsConnectionEstablished(false);
    }
  };

  const stopListening = (): void => {
    if (sessionRef.current) {
      sessionRef.current.stop();
      sessionRef.current = null;
    }
    
    setIsListening(false);
    setIsProcessing(false);
    setIsConnectionEstablished(false);
    setStatusMessage('Ready to start');

    if (partialTranscript.trim()) {
      const finalText = formatCompletedTranscript(
        accumulatedTranscriptRef.current,
        partialTranscript
      );

      accumulatedTranscriptRef.current += finalText;
      setFullTranscript(accumulatedTranscriptRef.current);
      setPartialTranscript('');
    }
  };

  const clearTranscript = (): void => {
    setFullTranscript('');
    setPartialTranscript('');
    accumulatedTranscriptRef.current = '';
    bufferedDeltasRef.current = '';
  };

  const clearFinalTranscript = (): void => {
    setFullTranscript('');
  };

  const toggleListening = (): void => {
    if (isListening) {
      stopListening();
    } else {
      startListening();
    }
  };

  return {
    isListening,
    isProcessing,
    isMicAvailable,
    isMicPermissionGranted,
    isConnectionEstablished,
    connectionStatus,
    partialTranscript,
    fullTranscript,
    errorMessage,
    statusMessage,
    startListening,
    stopListening,
    clearTranscript,
    clearFinalTranscript,
    toggleListening,
    checkMicrophoneAvailability,
    requestMicrophonePermission
  };
};

export default useGPT4Transcription;